// Long-press → peek → drag tracker for phone surfaces, built on pointer
// events so it also works with a mouse under `?phone=1`.
//
// Gesture contract:
//   - press and release quickly            → nothing (the element's own
//     click handler fires: tap-to-add, tap-to-inspect)
//   - move before the hold timer fires     → cancel; native scroll wins
//   - hold ≥ holdMs without moving         → onActivate (peek)
//   - move after activation                → onMove (drag); the browser's
//     scroll is suppressed via a non-passive touchmove preventDefault,
//     which is only honored because no scroll has started yet
//   - release                              → onEnd(x, y, wasActive)
//
// After an activated press the next click (synthesized from the same
// pointerup) is squelched at the window capture phase so tap handlers on
// the pressed element don't also fire.
export class PhoneLongPress {
  constructor({ holdMs = 350, tolerancePx = 12 } = {}) {
    this.holdMs = holdMs;
    this.tolerancePx = tolerancePx;
    this._timer = null;
    this._cb = null;
    this._active = false;
  }

  begin(e, cb) {
    if (this._cb || e.isPrimary === false) return;
    this._cb = cb;
    this._active = false;
    this._startX = e.clientX;
    this._startY = e.clientY;
    this._timer = setTimeout(() => {
      this._timer = null;
      this._active = true;
      document.addEventListener('touchmove', this._blockScroll, { passive: false });
      this._cb.onActivate?.(this._startX, this._startY);
    }, this.holdMs);
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('pointercancel', this._onCancel);
  }

  _blockScroll = (e) => {
    e.preventDefault();
  };

  _onMove = (e) => {
    if (!this._cb) return;
    if (!this._active) {
      const dx = e.clientX - this._startX;
      const dy = e.clientY - this._startY;
      if (dx * dx + dy * dy > this.tolerancePx * this.tolerancePx) this._cleanup();
      return;
    }
    this._cb.onMove?.(e.clientX, e.clientY);
  };

  _onUp = (e) => {
    const cb = this._cb;
    const wasActive = this._active;
    this._cleanup();
    if (wasActive) squelchNextClick();
    cb?.onEnd?.(e.clientX, e.clientY, wasActive);
  };

  _onCancel = () => {
    const cb = this._cb;
    this._cleanup();
    cb?.onEnd?.(null, null, false);
  };

  _cleanup() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
    this._cb = null;
    this._active = false;
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    window.removeEventListener('pointercancel', this._onCancel);
    document.removeEventListener('touchmove', this._blockScroll);
  }
}

function squelchNextClick() {
  const squelch = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  window.addEventListener('click', squelch, { capture: true, once: true });
  // The click may never fire (touch after preventDefault). Drop the
  // squelcher before it can eat an unrelated later click.
  setTimeout(() => window.removeEventListener('click', squelch, { capture: true }), 400);
}
